import { DateTime } from 'luxon';
import {
  type FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import './styles/styles.css';
import { ErrorMessage } from '@hookform/error-message';
import { Button, Col, Form, Overlay, Popover, Row } from 'react-bootstrap';
import { Clipboard, Eye, Pencil } from 'react-bootstrap-icons';
import { Controller, useForm } from 'react-hook-form';
import {
  MultiSelect,
  type Option as MultiSelectOption,
} from 'react-multi-select-component';
import { useNavigate } from 'react-router-dom';
import CheckIcon from '../../assets/check.svg';
import CustomFieldsIcon from '../../assets/custom-fields.svg';
import DocRetry from '../../assets/doc-retry.svg';
import DocSuccess from '../../assets/doc-success.svg';
import DocWarning from '../../assets/doc-warning.svg';
import DownBlackIcon from '../../assets/down-black.svg';
import DownloadIcon from '../../assets/download.svg';
import FeeIcon from '../../assets/fee.svg';
import ForwordIcon from '../../assets/forword.svg';
import GuestPortal from '../../assets/guest-portal.svg';
import ReportIcon from '../../assets/report.svg';
import RingSpinner from '../../assets/ring-spinner.svg';
import RoomIcon from '../../assets/room.svg';
import UnCheckIcon from '../../assets/uncheck.svg';
import UpSellIcon from '../../assets/upsells.svg';
import { unknownErrorMessage } from '../../constants/generalData';
import { config } from '../../modules/config';
import { ReservationPackageContext } from '../../modules/context/guestPackageContext';
import { UserPropertiesContext } from '../../modules/context/userPropertiesContext';
import { PackageRequestStatus } from '../../modules/enums/status';
import {
  useAuth,
  useMutationRequest,
  useNotifications,
  useQueryRequest,
  useRequest,
  useRequestBuffer,
} from '../../modules/hooks';
import type {
  CombinedRegistrationStep,
  DynamicQuestionPayload,
  GuestPackageResponse,
  MewsReservation,
  Reservation,
  ReservationRegistrationStep,
} from '../../modules/interfaces';
import { endpoints, routes } from '../../modules/mappers/urls';
import { buildRequestUrl } from '../../modules/utils/buildRequestUrl';
import { Can } from '../../modules/utils/can';
import { formatDateTime } from '../../modules/utils/dateFormat';
import { isDesynchronized } from '../../modules/utils/isDesynchronized';
import { mergeRegistrationSteps } from '../../modules/utils/registrationSteps';
import {
  getApiArrayBufferErrorMessage,
  getApiErrorMessage,
} from '../../modules/utils/transform';
import { SynchronizeModal } from '../Modals/SynchronizeModal/SynchronizeModal';

interface BookingDetailsProps {
  selectedReservation: MewsReservation;
  reservationPackages?: GuestPackageResponse[];
  fromReservationDetails: boolean;
}

enum downloadAgreementStates {
  IDLE = 'Download',
  RETRIEVING = 'Retrieving data...',
  GENERATING = 'Generating document...',
  DOWNLOADING = 'Starting download...',
  SUCCESS = 'Downloaded',
  ERROR = 'Retry download',
  NOT_SIGNED = 'Not signed',
}

type FormValues = {
  [key: string]: string;
};

const BookingDetails: FunctionComponent<BookingDetailsProps> = ({
  selectedReservation,
  reservationPackages,
  fromReservationDetails = false,
}: BookingDetailsProps) => {
  const [isFeeVisible, setIsFeeVisible] = useState<boolean>(true);
  const [isCustomField, setIsCustomFiels] = useState<boolean>(true);
  const [packages, setPackages] = useState<GuestPackageResponse[]>([]);
  const [showSynchronizeModal, setShowSynchronizeModal] = useState(false);
  const [dynamicQuestions, setDynamicQuestions] = useState<DynamicQuestionPayload[]>([]);
  const [guestPackageId, setGuestPackageId] = useState('');
  const [guestReservation, setGuestReservation] = useState<Reservation | undefined>();
  const { credentialsInfo } = useAuth()!;
  const navigate = useNavigate();
  const { setSimpleToasts } = useNotifications()!;
  const [statusIndex, setStatusIndex] = useState<number | undefined>();
  const [readOnly, setReadOnly] = useState(true);
  const signedAgreementPdfUrl = `${endpoints.GUEST_RESERVATIONS}/${selectedReservation.Id}/pdf/signed-agreement`;
  const [pdfDownloadState, setPdfDownloadState] = useState<downloadAgreementStates>(
    downloadAgreementStates.IDLE,
  );

  const reservationPackageList = useContext(ReservationPackageContext);
  const { getReservationPackage } = reservationPackageList!;

  const [combinedSteps, setCombinedSteps] = useState<CombinedRegistrationStep[]>([]);

  const { propertyRegistrationStep, selectedProperty } =
    useContext(UserPropertiesContext)!;

  const dynamicAnswersUrl = buildRequestUrl(
    endpoints.DYNAMIC_QUESTION_ANSWERS_BY_RESERVATION,
    {
      propertyId: selectedProperty?.property?.uuid || '',
      reservationId: selectedReservation.Id || '',
    },
  );

  const {
    data: dynamicQuestionResult,
    loading: loadingDynamicQuestion,
    error: errorDynamicQuestion,
  } = useQueryRequest<DynamicQuestionPayload[]>(dynamicAnswersUrl, {
    authToken: credentialsInfo?.token,
    propertyId: selectedProperty?.property.uuid,
  });

  const createDynamicAnswersUrl = buildRequestUrl(endpoints.DYNAMIC_ANSWERS, {
    propertyId: selectedProperty?.property?.uuid || '',
    reservationId: selectedReservation?.Id || '',
  });

  const [
    {
      data: createQuestionResult,
      loading: loadingCreateQuestion,
      error: errorCreateQuestion,
    },
    createOrUpdateDynamicQuestion,
  ] = useMutationRequest<DynamicQuestionPayload[]>(
    createDynamicAnswersUrl,
    'patch',
    {
      authToken: credentialsInfo?.token,
      propertyId: selectedProperty?.property.uuid,
    },
    {
      manual: true,
    },
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: dynamicQuestions.reduce((acc, q) => {
      acc[`question_${q.uuid}`] = q.answer || '';
      return acc;
    }, {} as FormValues),
  });

  const [popoverState, setPopoverState] = useState<{
    show: boolean;
    value: string;
    target: HTMLElement | null;
  }>({ show: false, value: '', target: null });
  const popoverRef = useRef<HTMLDivElement>(null);

  const handleFieldDoubleClick = (
    e: React.MouseEvent<HTMLElement>,
    fieldName: string,
  ) => {
    const value = getValues(fieldName) || '';
    if (!value) return;
    setPopoverState({ show: true, value, target: e.currentTarget as HTMLElement });
  };

  const formatDate = (dateString: string) => {
    const tz = process.env.REACT_APP_LODGING_LOCATION_TIMEZONE;

    return DateTime.fromISO(dateString, { zone: tz }).toLocaleString(DateTime.DATE_FULL);
  };

  const renderVerificationItem = (completed: boolean, title: string) => {
    return (
      <div className="verification-item">
        {completed ? (
          <img alt="check" src={CheckIcon} />
        ) : (
          <img alt="uncheck" src={UnCheckIcon} />
        )}
        <div className="verification-item-text">{title}</div>
      </div>
    );
  };

  const renderCustomField = (value: string, title: string) => {
    return (
      <div className="info-container">
        <div className="fee-text-title">{title}</div>
        <div className="row-view">
          <div className="info-text">{value}</div>
        </div>
      </div>
    );
  };

  const URL = `${endpoints.RESERVATION_PACKAGES}/${guestPackageId}`;
  const [
    { data: guestPackageData, error: errorPackageData, loading: loadingPackageData },
    updateReservationPackageState,
  ] = useRequest<GuestPackageResponse>(
    URL,
    'patch',
    {
      authToken: credentialsInfo?.token,
      propertyId: selectedProperty?.property.uuid,
    },
    { manual: true, autoCancel: false },
  );

  const url = `${endpoints.RESERVATION}/${selectedReservation?.Id}/registration-steps`;
  const [{ data: reservationRegistrationStepResult }] = useRequest<
    ReservationRegistrationStep[]
  >(url, 'get', {
    authToken: credentialsInfo?.token,
    propertyId: selectedProperty?.property.uuid,
  });

  const guestReservationURL = `${endpoints.MEWS_RESERVATIONS}/${selectedReservation.Id}/details`;
  const [
    { data: guestReservationData, loading: loadingReservationDetails },
    getReservationData,
  ] = useRequest<Reservation>(guestReservationURL, 'get', {
    authToken: credentialsInfo?.token,
    propertyId: selectedProperty?.property.uuid,
  });

  const [{ data: pdfDataResponse, error: pdfDataError }, downloadAgreementPDF] =
    useRequestBuffer<ArrayBuffer>(
      signedAgreementPdfUrl,
      'get',
      {
        authToken: credentialsInfo?.token,
        propertyId: selectedProperty?.property.uuid,
      },
      {
        manual: true,
      },
    );

  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  // pdf download handler
  const handleDownload = (pdfData: string) => {
    if (!pdfData) {
      setSimpleToasts({
        type: 'danger',
        message: 'Generating agreement failed. Please try again.',
        show: true,
      });
      setPdfDownloadState(downloadAgreementStates.ERROR);
      return;
    }
    setPdfDownloadState(downloadAgreementStates.DOWNLOADING);
    const linkSource = `data:application/pdf;base64,${pdfData}`;
    const downloadLink = document.createElement('a');
    const fileName = `${selectedReservation.Id}_signedAgreement.pdf`;

    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
    setPdfDownloadState(downloadAgreementStates.SUCCESS);

    // reset download state after 15 seconds
    setTimeout(() => {
      setPdfDownloadState(downloadAgreementStates.IDLE);
    }, 15000);
  };

  const executeDownload = () => {
    if (
      pdfDownloadState !== downloadAgreementStates.IDLE &&
      pdfDownloadState !== downloadAgreementStates.ERROR
    ) {
      return;
    }
    setPdfDownloadState(downloadAgreementStates.RETRIEVING);
    downloadAgreementPDF();
  };
  const handleRefresh = () => {
    getReservationData({
      headers: {
        authorization: credentialsInfo?.token,
        'property-id': selectedProperty?.property.uuid,
      },
    });
  };

  useEffect(() => {
    if (!dynamicQuestions.length) return;

    const newValues = dynamicQuestions.reduce((acc, q) => {
      acc[`question_${q.uuid}`] = q.answer || '';
      return acc;
    }, {} as FormValues);

    reset(newValues);
  }, [dynamicQuestions]);

  useEffect(() => {
    if (dynamicQuestionResult) {
      setDynamicQuestions(dynamicQuestionResult.result);
    }
  }, [dynamicQuestionResult]);

  useEffect(() => {
    if (!reservationRegistrationStepResult || !reservationRegistrationStepResult?.result)
      return;

    const merged = mergeRegistrationSteps(
      reservationRegistrationStepResult.result ?? [],
      propertyRegistrationStep ?? [],
    );
    setCombinedSteps(merged);
  }, [reservationRegistrationStepResult, propertyRegistrationStep]);

  useEffect(() => {
    if (!fromReservationDetails || !reservationPackages) return;

    setPackages(reservationPackages);
  }, [reservationPackages]);

  useEffect(() => {
    if (pdfDataResponse) {
      setPdfDownloadState(downloadAgreementStates.GENERATING);
      const base64Data = arrayBufferToBase64(pdfDataResponse);
      handleDownload(base64Data);
    }
  }, [pdfDataResponse]);

  useEffect(() => {
    if (pdfDataError) {
      const errMessage = getApiArrayBufferErrorMessage(pdfDataError);

      if (errMessage === 'No guest reservation matching the provided id was found.') {
        setPdfDownloadState(downloadAgreementStates.NOT_SIGNED);
        return;
      }

      setSimpleToasts({
        type: 'danger',
        message: 'Retrieving agreement failed. Please try again.',
        show: true,
      });
      setPdfDownloadState(downloadAgreementStates.ERROR);
    }
  }, [pdfDataError]);

  useEffect(() => {
    if (errorCreateQuestion) {
      const message = getApiErrorMessage(errorCreateQuestion);
      setSimpleToasts({ type: 'danger', message, show: true });
    }

    if (errorDynamicQuestion) {
      setSimpleToasts({
        type: 'danger',
        message: errorDynamicQuestion.message,
        show: true,
      });
    }

    if (createQuestionResult) {
      setSimpleToasts({
        type: 'success',
        message: 'Questions data saved successfully',
        show: true,
      });
    }
  }, [createQuestionResult, errorCreateQuestion, errorDynamicQuestion]);

  useEffect(() => {
    if (!guestReservationData || fromReservationDetails) return;

    setPackages(guestReservationData.result.reservationPackages);
  }, [guestReservationData]);

  useEffect(() => {
    if (!guestReservationData) return;

    setGuestReservation(guestReservationData?.result);

    if (fromReservationDetails && guestPackageData) {
      setPackages(guestReservationData.result.reservationPackages);
    }
  }, [guestReservationData]);

  const onFeeExpandClick = useCallback(() => {
    setIsFeeVisible(!isFeeVisible);
  }, [isFeeVisible]);

  const onCustomFieldExpandClick = useCallback(() => {
    setIsCustomFiels(!isCustomField);
  }, [isCustomField]);

  const onConfirm = (index: number, action: string) => {
    setPackages((prevPackages) => {
      const newPackage = prevPackages.map((packageItem, j) => {
        const newPackageItem = { ...packageItem };
        if (index === j) {
          newPackageItem.status = action;
        }
        return newPackageItem;
      });
      return newPackage;
    });

    setStatusIndex(undefined);
  };

  useEffect(() => {
    if (
      loadingPackageData ||
      (!guestPackageData && !errorPackageData) ||
      statusIndex === undefined
    )
      return;

    if (guestPackageData && !errorPackageData && guestPackageData.result.status) {
      getReservationPackage({
        headers: {
          authorization: credentialsInfo?.token,
          'property-id': selectedProperty?.property.uuid,
        },
      });
    }

    if (guestPackageData) {
      getReservationData({
        headers: {
          authorization: credentialsInfo?.token,
          'property-id': selectedProperty?.property.uuid,
        },
      });
    }

    if (errorPackageData) {
      setSimpleToasts({
        message: errorPackageData.response?.data.error.message || unknownErrorMessage,
        type: 'danger',
        show: true,
      });

      updateReservationPackageState({
        data: { status: 'FAILED' },
        headers: {
          authorization: credentialsInfo?.token,
          'property-id': selectedProperty?.property.uuid,
        },
      });
      onConfirm(statusIndex, 'FAILED');
      getReservationPackage();
    }
  }, [guestPackageData, errorPackageData]);

  const onSubmit = (reservationStatus: string, guestPackageId: string, index: number) => {
    setGuestPackageId(guestPackageId);
    setStatusIndex(index);

    const url = `${endpoints.RESERVATION_PACKAGES}/${guestPackageId}`;
    updateReservationPackageState({
      url,
      data: { status: reservationStatus },
      headers: {
        authorization: credentialsInfo?.token,
        'property-id': selectedProperty?.property.uuid,
      },
    });
  };

  const copyToClipboardHandler = (reservationUrl?: string) => {
    if (!reservationUrl) {
      setSimpleToasts({
        type: 'warning',
        message: 'There is no URL for this reservation',
        show: true,
      });
      return;
    }

    navigator.clipboard.writeText(reservationUrl);
    setSimpleToasts({
      type: 'success',
      message: 'Reservation URL copied successfully',
      show: true,
    });
  };

  const renderUpsellView = (index: number, item: GuestPackageResponse) => {
    return (
      <div className="upsells-container">
        <div className="checkin-container">
          <div className="checkin-detail-container">
            <div className="room-details">
              {formatDateTime(item?.packages?.initialDate?.toString() || '')}
            </div>

            <div className="room-name"> {item.packages.name}</div>
          </div>
          <div className="status-detail-container">
            <div>
              <div className="room-details">Status</div>

              <div
                className="paid-text"
                style={{
                  color: item.status === 'APPROVED' ? '#21C413' : '#6B7280',
                }}
              >
                {item.status === 'APPROVED' ? 'PAID' : 'UNPAID'}
              </div>
            </div>
          </div>
        </div>
        <div className="devider" />

        <Can I="update" a="packages">
          {item.status === PackageRequestStatus.PENDING ? (
            <div className="row-view">
              <div className="confirm-btn">
                <div
                  className="revoke-text"
                  onClick={() => onSubmit('APPROVED', item.uuid, index)}
                >
                  Confirm
                </div>
              </div>
              <div className="revoke-btn">
                <div
                  className="revoke-text"
                  onClick={() => onSubmit('DENIED', item.uuid, index)}
                >
                  Deny
                </div>
              </div>
            </div>
          ) : (
            <div
              className="upsell-status-text"
              style={{
                color: item.status === 'APPROVED' ? '#0CA925' : '#DB453C',
              }}
            >
              {item.status === 'APPROVED'
                ? 'This add-on is confirmed'
                : 'This add-on is denied'}
            </div>
          )}
        </Can>
      </div>
    );
  };

  const sendDynamicQuestions = (data: FormValues) => {
    const answersList = Object.entries(data).map(([key, value]) => {
      const questionId = key.replace(/^question_/, '');
      return { questionId, answer: value };
    });

    createOrUpdateDynamicQuestion(answersList);
  };

  return (
    <div className="reservation-detail-container">
      <Row>
        <Col xs={8}>
          <div className="room-detail-container">
            <img className="room-image" alt="room" src={RoomIcon} />
            <div className="room-name-container">
              <div className="room-name">
                {selectedReservation.Resource
                  ? selectedReservation.Resource.ResourceName
                  : 'The Classic Queen'}
              </div>
              <div className="room-details">
                {selectedReservation.Resource?.Data?.Value?.FloorOrdinalNumber ||
                  'First Floor'}
              </div>
            </div>
          </div>
        </Col>
        <Col xs={4} style={{ display: 'flex', justifyContent: 'right' }}>
          {isDesynchronized(selectedReservation) && (
            <Button
              className="redirect-button"
              onClick={() => setShowSynchronizeModal(true)}
              size="sm"
            >
              <span className="d-flex align-items-center mr-10">Synchronize&nbsp;</span>
            </Button>
          )}

          <Button
            className="redirect-button"
            onClick={() => {
              if (selectedReservation) {
                const {
                  Id: reservationId,
                  State,
                  Customer,
                  Resource,
                  ScheduledStartUtc,
                  EndUtc,
                  CreatedUtc: createdAt,
                } = selectedReservation;
                const amountOfNights =
                  EndUtc && ScheduledStartUtc
                    ? DateTime.fromISO(EndUtc)
                        .startOf('day')
                        .diff(DateTime.fromISO(ScheduledStartUtc).startOf('day'), 'days')
                        .days
                    : 0;
                const room = Resource
                  ? `${Resource.Name} -  ${Resource.ResourceName}`
                  : '';

                navigate(
                  routes.RESERVATION_DETAILS.replace(':reservationId', reservationId),
                  {
                    state: {
                      reservationId,
                      status: State,
                      phoneNumber: Customer?.Phone || '',
                      email: Customer?.Email || '',
                      guestName: `${Customer?.FirstName} ${Customer?.LastName}`,
                      room,
                      checkIn: ScheduledStartUtc,
                      checkOut: EndUtc,
                      createdAt,
                      amountOfNights,
                    },
                  },
                );
              }
            }}
          >
            Details
          </Button>
        </Col>
      </Row>

      <div className="checkin-container">
        <div className="checkin-detail-container">
          <div className="room-details">Check-in</div>
          <div className="room-name">
            {formatDate(selectedReservation.ScheduledStartUtc)}
          </div>
        </div>
        <div className="checkin-detail-container">
          <div className="room-details">Room number</div>
          <div className="room-name">
            {selectedReservation.Resource ? selectedReservation.Resource.Name : 3}
          </div>
        </div>
      </div>
      <div className="checkin-container">
        <div className="checkin-detail-container">
          <div className="room-details">Check-out</div>
          <div className="room-name">{formatDate(selectedReservation.EndUtc)}</div>
        </div>
        <div className="checkin-detail-container">
          <div className="room-details">Guests</div>
          <div className="room-name">
            {selectedReservation.PersonCounts.reduce(
              (accumulator, currentValue) => accumulator + currentValue.Count,
              0,
            )}
          </div>
        </div>
      </div>
      <div className="devider" />
      <div className="verification-step-title">Verification steps</div>
      <div className="verification-container">
        <div className="checkin-detail-container">
          <Row>
            {combinedSteps.map((step) => (
              <Col xs={6}>{renderVerificationItem(step.isCompleted, step.name)}</Col>
            ))}

            <Col xs={6}>
              {renderVerificationItem(
                guestReservation?.startDate &&
                  guestReservation?.safetyDepositPaymentStatus === 'PAID' &&
                  !!guestReservation?.guestReservation?.signature &&
                  !!guestReservation?.guestReservation?.guest?.frontIdCard
                  ? DateTime.fromISO(guestReservation?.startDate?.toString() || '') <=
                      DateTime.now()
                  : false,
                'Check In',
              )}
            </Col>
          </Row>
        </div>
      </div>
      <div className="devider" />
      <div className="checkin-container">
        <div className="row-view">
          <img alt="report" src={ReportIcon} />
          <div className="verification-report">Verification Report</div>
        </div>
        {pdfDownloadState === downloadAgreementStates.IDLE && (
          <div className="download-btn" onClick={executeDownload}>
            <img alt="report" src={DownloadIcon} />
            <div className="download-text">{pdfDownloadState}</div>
          </div>
        )}
        {pdfDownloadState !== downloadAgreementStates.IDLE &&
          pdfDownloadState !== downloadAgreementStates.ERROR &&
          pdfDownloadState !== downloadAgreementStates.SUCCESS &&
          pdfDownloadState !== downloadAgreementStates.NOT_SIGNED && (
            <div className="download-btn is-downloading">
              <img alt="report" src={RingSpinner} />
              <div className="download-text">{pdfDownloadState}</div>
            </div>
          )}
        {pdfDownloadState === downloadAgreementStates.ERROR && (
          <div className="download-btn is-errored" onClick={executeDownload}>
            <img alt="report" src={DocRetry} />
            <div className="download-text">{pdfDownloadState}</div>
          </div>
        )}
        {pdfDownloadState === downloadAgreementStates.SUCCESS && (
          <div className="download-btn is-downloaded">
            <img alt="report" src={DocSuccess} />
            <div className="download-text">{pdfDownloadState}</div>
          </div>
        )}
        {pdfDownloadState === downloadAgreementStates.NOT_SIGNED && (
          <div className="download-btn is-unsigned">
            <img alt="report" src={DocWarning} />
            <div className="download-text">{pdfDownloadState}</div>
          </div>
        )}
      </div>
      {(pdfDownloadState === downloadAgreementStates.ERROR && (
        <div className="text-danger mt-2">
          Agreement download failed. Please try again.
        </div>
      )) ||
        null}
      {(pdfDownloadState === downloadAgreementStates.NOT_SIGNED && (
        <div className="text-muted mt-2">Agreement not signed yet.</div>
      )) ||
        null}
      <div className="devider" />
      <div className="checkin-container">
        <div className="row-view">
          <img alt="report" src={GuestPortal} />
          <div className="verification-report">Guest Portal Access</div>
        </div>
        <div className="checkin-container">
          <div
            className="copy-reservation-url-btn"
            onClick={() => copyToClipboardHandler(guestReservation?.reservationUrl)}
          >
            <div className="revoke-text">
              <Clipboard />
            </div>
          </div>
          <div className="revoke-btn">
            <div className="revoke-text">Revoke</div>
          </div>
        </div>
      </div>
      {packages.length > 0 && (
        <>
          <div className="devider" />

          <div className="row-view">
            <img alt="upsell" src={UpSellIcon} />
            <div className="verification-report">Add-ons</div>
          </div>
        </>
      )}
      {packages.length > 0 &&
        !loadingReservationDetails &&
        packages.map((item, index) => {
          return renderUpsellView(index, item);
        })}
      {selectedReservation?.Prices?.taxes && (
        <>
          <div className="devider" />
          <div className="fee-container">
            <div className="row-view">
              <img alt="fee" src={FeeIcon} />
              <div className="verification-report">TAXES</div>
            </div>
            <div className="row-view">
              <div onClick={onFeeExpandClick} className="fee-expand-btn">
                <img alt="down" src={isFeeVisible ? DownBlackIcon : ForwordIcon} />
              </div>
            </div>
          </div>
        </>
      )}
      {isFeeVisible &&
        selectedReservation?.Prices?.taxes &&
        selectedReservation?.Prices?.taxes.map((item) => (
          <div className="price-info-container">
            <div className="price-info-title">{item.name}</div>
            <div className="price-info-value">{`$${item.value.toFixed(2)}`}</div>
          </div>
        ))}
      <div className="devider" />
      <div className="fee-container">
        <div className="row-view">
          <img alt="fee" src={CustomFieldsIcon} />
          <div className="verification-report">Custom Fields</div>
        </div>
        <div className="row-view">
          <div
            className={readOnly ? 'edit-questions-btn' : 'read-only-questions-btn'}
            onClick={() => setReadOnly(!readOnly)}
          >
            <div className="revoke-text">
              {readOnly ? <Pencil size={12} /> : <Eye size={12} />}
            </div>
          </div>
          <div className="fee-text" style={{ color: '#14BF59' }}>
            {dynamicQuestions.length} Fields
          </div>
          <div onClick={onCustomFieldExpandClick} className="fee-expand-btn">
            <img alt="down" src={isCustomField ? DownBlackIcon : ForwordIcon} />
          </div>
        </div>
      </div>
      {/*
       */}

      {showSynchronizeModal && (
        <SynchronizeModal
          selectedReservations={[selectedReservation]}
          show={showSynchronizeModal}
          handleClose={() => setShowSynchronizeModal(false)}
          refreshHandler={handleRefresh}
          fromDetails
        />
      )}

      {isCustomField &&
        (config.dynamicQuestions.showQuestions ? (
          <div className="questions-container">
            <Form onSubmit={handleSubmit(sendDynamicQuestions)}>
              {dynamicQuestions.map((questionValue) => (
                <Row className="mb-3" key={questionValue.uuid}>
                  <Col sm={4}>
                    <div className="fee-text-title">{questionValue.question}</div>
                  </Col>
                  <Col
                    sm={8}
                    onDoubleClick={(e) =>
                      handleFieldDoubleClick(e, `question_${questionValue.uuid}`)
                    }
                  >
                    {questionValue.type === 'multi_select' ? (
                      <Controller
                        control={control}
                        name={`question_${questionValue.uuid}`}
                        rules={{
                          required: questionValue.isRequired
                            ? 'This field is required'
                            : false,
                        }}
                        render={({ field }) => {
                          const selected: MultiSelectOption[] = field.value
                            ? field.value
                                .split(', ')
                                .map((v: string) => ({ label: v, value: v }))
                            : [];
                          const options: MultiSelectOption[] =
                            questionValue.selectableValues?.map((opt) => ({
                              label: opt,
                              value: opt,
                            })) || [];
                          return (
                            <MultiSelect
                              options={options}
                              value={selected}
                              onChange={(values: MultiSelectOption[]) => {
                                field.onChange(values.map((v) => v.value).join(', '));
                              }}
                              labelledBy="Select options"
                              disabled={readOnly}
                            />
                          );
                        }}
                      />
                    ) : questionValue.type === 'select' ? (
                      <Form.Select
                        disabled={readOnly}
                        {...register(`question_${questionValue.uuid}`, {
                          required: questionValue.isRequired
                            ? 'This field is required'
                            : false,
                        })}
                      >
                        <option value="">Select an option</option>
                        {questionValue.selectableValues?.map((opt, i) => (
                          <option key={i} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <Form.Control
                        disabled={readOnly}
                        type={questionValue.type}
                        {...register(`question_${questionValue.uuid}`, {
                          required: questionValue.isRequired
                            ? 'This field is required'
                            : false,
                        })}
                        placeholder={questionValue.type === 'text' ? 'Enter value' : ''}
                      />
                    )}
                    <ErrorMessage
                      errors={errors}
                      name={`question_${questionValue.uuid}`}
                      render={({ message }) => (
                        <span className="error-text">{message}</span>
                      )}
                    />
                  </Col>
                </Row>
              ))}

              <Row className="mb-3">
                <Col style={{ display: 'flex', justifyContent: 'end' }}>
                  <Button type="submit" disabled={readOnly || loadingCreateQuestion}>
                    {loadingCreateQuestion ? 'Loading...' : 'Submit'}
                  </Button>
                </Col>
              </Row>
            </Form>
            <Overlay
              show={popoverState.show}
              target={popoverState.target}
              placement="top"
              rootClose
              onHide={() => setPopoverState((prev) => ({ ...prev, show: false }))}
              container={popoverRef}
            >
              <Popover>
                <Popover.Body
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    userSelect: 'text',
                  }}
                >
                  {popoverState.value}
                </Popover.Body>
              </Popover>
            </Overlay>
            <div ref={popoverRef} />
          </div>
        ) : (
          <>
            {renderCustomField(
              `${selectedReservation.Customer?.FirstName} ${selectedReservation.Customer?.LastName}`,
              'Guest name (s)',
            )}
            {renderCustomField(
              guestReservation?.guestReservation?.guest?.licensePlate || '-',
              'License Plate',
            )}
            {renderCustomField(
              guestReservation?.guestReservation?.estimatedTimeOfArrival || '-',
              'Estimated time of arrival',
            )}
            {renderCustomField(
              guestReservation?.guestReservation?.howDidYouHearAboutUs || '-',
              'How did you hear about us?',
            )}{' '}
          </>
        ))}
    </div>
  );
};

export default BookingDetails;
